import {
  applyFilterClauseToRow,
  type FilterClause,
  type SortClause,
} from "../_common";
import { FILTER_TYPE_OBJ, SORT_DIRECTION_OBJ } from "../_deps";
import { PORTFOLIO_FILTER_COLUMN_OBJ } from "./portfolio-page-formatters";
/** Client-side sort + paginate for SmartTable (manualPagination/sorting mode). */
export function sliceData<T extends object>(
  all: T[],
  sort: SortClause[],
  page: number,
  pageSize: number,
): T[] {
  if (all.length === 0) return [];
  let result = [...all];
  if (sort.length > 0) {
    for (const clause of sort) {
  const dir = clause.direction === SORT_DIRECTION_OBJ.DESC ? -1 : 1;
      result.sort((a, b) => {
        const av = Reflect.get(a, clause.column);
        const bv = Reflect.get(b, clause.column);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av).localeCompare(String(bv)) * dir;
      });
    }
  }
  const start = (page - 1) * pageSize;
  return result.slice(start, start + pageSize);
}

/**
 * Apply client-side FilterClause[] to a row array BEFORE pagination.
 * Server-side clauses (accountId select-single, date date-range) are
 * excluded — they are sent to the backend via extractServerFilters and
 * must not be re-applied on the client.
 */
export function applyClientFilters<T extends object>(
  rows: T[],
  clientFilters: FilterClause[],
): T[] {
  if (clientFilters.length === 0) return rows;
  return rows.filter((row) =>
    clientFilters.every((clause) =>
      applyFilterClauseToRow(Object.fromEntries(Object.entries(row)), clause),
    ),
  );
}

/**
 * Extract server-side filter params (accountId, from, to) from FilterClause[].
 * Used to pass ?accountId=&from=&to= query params to the backend.
 * Only select-single on "accountId" and date-range on "date" are server-side;
 * all other FilterClause types remain client-side in SmartTable.
 */
export interface ServerFilters {
  accountId?: string;
  from?: string;
  to?: string;
}

/** Filters whose meaning is shared by positions and transaction history. */
export function isPortfolioServerFilter(filter: FilterClause): boolean {
  return (filter.type === FILTER_TYPE_OBJ.SELECT_SINGLE && filter.column === PORTFOLIO_FILTER_COLUMN_OBJ.ACCOUNT_ID) ||
    (filter.type === FILTER_TYPE_OBJ.DATE_RANGE && filter.column === PORTFOLIO_FILTER_COLUMN_OBJ.DATE);
}

export function extractServerFilters(filters: FilterClause[]): ServerFilters {
  const result: ServerFilters = {};
  for (const f of filters) {
    if (f.type === FILTER_TYPE_OBJ.SELECT_SINGLE && f.column === PORTFOLIO_FILTER_COLUMN_OBJ.ACCOUNT_ID && f.value) {
      result.accountId = f.value;
    }
    if (f.type === FILTER_TYPE_OBJ.DATE_RANGE && f.column === PORTFOLIO_FILTER_COLUMN_OBJ.DATE) {
      if (f.from) result.from = f.from;
      if (f.to) result.to = f.to;
    }
  }
  return result;
}

